/**
 * The caller's chat privacy toggles — server-backed, shared across devices.
 *
 *   const { privacy, setReadReceipts, setShowStatus, isLoading } = useChatPrivacy()
 *
 * Both flags are ENFORCED server-side; this hook exists so the settings UI can
 * reflect and change them, and so the client can apply the one half of read-
 * receipt reciprocity that is legitimately client-side (see below).
 *
 * **Reciprocity.** Turning read receipts off has two halves:
 *   - *others can't see your ticks* — server-enforced. It drops your read cursor
 *     and redacts the persisted one, so a hacked client gains nothing.
 *   - *you can't see theirs* — applied here, by `selectUiConversations` hiding
 *     blue ticks while `readReceipts` is false. This half only withholds
 *     information from yourself, so client-side is the honest place for it.
 *
 * "Show status" has no client half: it's entirely server-enforced, and is
 * deliberately NOT reciprocal — hiding your own status still lets you see
 * everyone else's.
 */

import { useCallback, useEffect, useState } from 'react'
import { useAuthStore } from '@/features/auth/store/auth.store'
import { getChatPrivacy, setChatPrivacy } from '@/sdk/instavcMessenger-instance'
import type { PrivacySettings } from '@/sdk/instavcMessenger'
import { setHideOthersReadReceipts } from '../store/messenger.store'

const DEFAULTS: PrivacySettings = { readReceipts: true, showStatus: true }

/**
 * Last known settings + the in-flight fetch, shared across mounts.
 *
 * The hook runs on the chat surface (so reciprocity applies wherever ticks
 * render) AND in the settings tab, which is a child of it — without this they'd
 * each issue their own GET and the settings tab would briefly show defaults
 * instead of the values already loaded.
 */
let cached: PrivacySettings | null = null
let inflight: Promise<PrivacySettings> | null = null

function loadPrivacy(userId: string): Promise<PrivacySettings> {
  if (cached) return Promise.resolve(cached)
  inflight ??= getChatPrivacy(userId)
    .then((p) => { cached = p; return p })
    .finally(() => { inflight = null })
  return inflight
}

/** Drop the shared cache — call on logout so the next user starts clean. */
export function resetChatPrivacyCache(): void {
  cached = null
  inflight = null
}

interface UseChatPrivacy {
  privacy: PrivacySettings
  setReadReceipts: (on: boolean) => Promise<void>
  setShowStatus: (on: boolean) => Promise<void>
  isLoading: boolean
  error: string | null
}

export function useChatPrivacy(): UseChatPrivacy {
  const me = useAuthStore((s) => s.user)
  const presenceId = useAuthStore((s) => s.presenceId)
  const [privacy, setPrivacy] = useState<PrivacySettings>(cached ?? DEFAULTS)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Apply the client half of reciprocity whenever the flag moves — including on
  // first load, so a user who turned receipts off in a previous session doesn't
  // see everyone's blue ticks until they touch the toggle again.
  useEffect(() => {
    setHideOthersReadReceipts(!privacy.readReceipts)
  }, [privacy.readReceipts])

  useEffect(() => {
    if (!me?.id || !presenceId) return
    let cancelled = false
    setIsLoading(true)
    loadPrivacy(me.id)
      .then((p) => { if (!cancelled) setPrivacy(p) })
      .catch(() => { if (!cancelled) setError('failed to load privacy settings') })
      .finally(() => { if (!cancelled) setIsLoading(false) })
    return () => { cancelled = true }
  }, [me?.id, presenceId])

  const patch = useCallback(
    async (change: Partial<PrivacySettings>) => {
      if (!me?.id) return
      const previous = privacy
      // Optimistic — a privacy switch should feel immediate.
      setPrivacy((p) => ({ ...p, ...change }))
      setError(null)
      try {
        // Render what the server says is in force, not what we asked for.
        const next = await setChatPrivacy(me.id, change)
        cached = next
        setPrivacy(next)
      } catch {
        setPrivacy(previous)
        setError('failed to save')
      }
    },
    [me?.id, privacy],
  )

  return {
    privacy,
    setReadReceipts: useCallback((on: boolean) => patch({ readReceipts: on }), [patch]),
    setShowStatus: useCallback((on: boolean) => patch({ showStatus: on }), [patch]),
    isLoading,
    error,
  }
}
